import { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { importLeadsCsv } from '../../api/leads';

interface ImportLeadsDialogProps {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}

interface ImportSummary {
  totalRows: number;
  created: number;
  failed: number;
  errors: Array<{ row: number; message: string }>;
  skippedDuplicates: number;
  duplicates: Array<{ row: number; message: string }>;
}

export const ImportLeadsDialog = ({ open, onClose, onImported }: ImportLeadsDialogProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);

  const handleClose = () => {
    setSummary(null);
    setError(null);
    onClose();
  };

  const handleFileSelected = async (file: File) => {
    setImporting(true);
    setError(null);
    try {
      const result = await importLeadsCsv(file);
      setSummary(result);
      if (result.created > 0) onImported();
    } catch {
      setError('Failed to import the file. Make sure it is a valid CSV.');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Import leads from CSV</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Columns: <code>firstName, lastName, email, phone, leadSource, country, notes, Ad ID Name</code>.
            The first row must be the header. <code>leadSource</code> must match an existing lead
            source name (e.g. "Website"). <code>Ad ID Name</code> is optional (the specific
            advertisement/campaign, kept separate from Lead Source) — the column may also be spelled
            "AdID Name", "Ad ID", "Ad Name" or "ad_id_name".
          </Typography>

          {error && <Alert severity="error">{error}</Alert>}

          {summary && (
            <Alert severity={summary.failed === 0 && summary.skippedDuplicates === 0 ? 'success' : 'warning'}>
              Processed {summary.totalRows} row(s): {summary.created} created, {summary.skippedDuplicates} skipped as
              duplicates, {summary.failed} failed.
            </Alert>
          )}

          {summary && summary.duplicates.length > 0 && (
            <Box sx={{ maxHeight: 150, overflowY: 'auto' }}>
              {summary.duplicates.map((d, i) => (
                <Typography key={i} variant="caption" color="text.secondary" component="div">
                  Row {d.row}: {d.message}
                </Typography>
              ))}
            </Box>
          )}

          {summary && summary.errors.length > 0 && (
            <Box sx={{ maxHeight: 150, overflowY: 'auto' }}>
              {summary.errors.map((e, i) => (
                <Typography key={i} variant="caption" color="error" component="div">
                  Row {e.row}: {e.message}
                </Typography>
              ))}
            </Box>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleFileSelected(file);
              e.target.value = '';
            }}
          />
          <Button variant="outlined" disabled={importing} onClick={() => fileInputRef.current?.click()}>
            {importing ? 'Importing…' : 'Choose CSV file'}
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
